import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, interval, Observable, Subscription } from 'rxjs';
import { map, skip, switchMap, take, tap } from 'rxjs/operators';
import { Filters } from 'src/app/models/filters.model';
import { DocumentService } from 'src/app/services/document.service';
import { PlatformService } from 'src/app/services/platform.service';
import { PodcastService } from 'src/app/services/podcast.service';
import { ToastService } from '../../services/toast.service';

@Component({
  selector: 'app-episode-player',
  templateUrl: './episode-player.page.html',
  styleUrls: ['./episode-player.page.scss'],
  standalone: false
})
export class EpisodePlayerPage implements OnInit {
  public episodeId: number;
  public episode$: Observable<any>;
  public downloadStatus = new BehaviorSubject('paused');
  public status$$ = new BehaviorSubject('not-playing');
  duration$: Observable<any>;
  position$: Observable<unknown>;
  positionDisplay$: Observable<string>;
  durationDisplay$: Observable<string>;
  episode$$ = new BehaviorSubject(null);
  episodeSubscription: Subscription;
  statusUpdateSubscription: Subscription;
  progress$: Observable<number>;
  public componentId: number;
  dataPodcastSubcription: Subscription;


  constructor(
    private route: ActivatedRoute,
    private podcastService: PodcastService,
    private toast: ToastService,
    private documentService: DocumentService,
    private router: Router,
    private platformService: PlatformService,
  ) {

  }

  ngOnInit() {

  }

  ionViewWillLeave() {
    this.podcastService.stop();
    this.episodeSubscription.unsubscribe();
    if (this.statusUpdateSubscription) {
      this.statusUpdateSubscription.unsubscribe();
    }
  }

  ionViewWillEnter() {

    console.log('episode player ionViewWillEnter');
    
    const filters: Filters = {
      searchKey: '',
      start: 0,
      length: 100
    };
    this.route.queryParams.subscribe(({ componentId, episodeId }) => {
      this.componentId = componentId;
      this.episodeId = episodeId;
      this.episodeSubscription = this.podcastService.getDataPodcast(this.componentId, filters)
        .pipe(switchMap(component => this.podcastService.getEpisode(this.episodeId).pipe(
          map(episode => ({
            ...episode,
            next$$: new BehaviorSubject(true),
            previous$$: new BehaviorSubject(true),
            component: component
          })),
          tap(episode => {
            // Configurar eventos del audio para el estado
            this.statusUpdateSubscription = new Subscription();
            
            const setupAudioEvents = () => {
              if (this.podcastService.audio) {
                console.log('Setting up audio events for:', this.podcastService.audio.src);
                
                const onPlay = () => {
                  console.log('Audio play event triggered');
                  this.status$$.next('playing');
                };
                const onPause = () => {
                  console.log('Audio pause event triggered');
                  this.status$$.next('not-playing');
                };
                const onLoadStart = () => {
                  console.log('Audio loadstart event triggered');
                  this.status$$.next('loading');
                };
                const onCanPlay = () => {
                  console.log('Audio canplay event triggered');
                  // Si el audio está reproduciéndose, actualizar el estado
                  if (!this.podcastService.audio.paused) {
                    this.status$$.next('playing');
                  }
                };
                const onEnded = () => {
                  console.log('Audio ended event triggered');
                  this.status$$.next('not-playing');
                  this.podcastService.stop();
                };
                
                // Remover listeners anteriores si existen
                this.podcastService.audio.removeEventListener('play', onPlay);
                this.podcastService.audio.removeEventListener('pause', onPause);
                this.podcastService.audio.removeEventListener('loadstart', onLoadStart);
                this.podcastService.audio.removeEventListener('canplay', onCanPlay);
                this.podcastService.audio.removeEventListener('ended', onEnded);
                
                // Agregar nuevos listeners
                this.podcastService.audio.addEventListener('play', onPlay);
                this.podcastService.audio.addEventListener('pause', onPause);
                this.podcastService.audio.addEventListener('loadstart', onLoadStart);
                this.podcastService.audio.addEventListener('canplay', onCanPlay);
                this.podcastService.audio.addEventListener('ended', onEnded);
                
                this.statusUpdateSubscription.add(() => {
                  this.podcastService.audio?.removeEventListener('play', onPlay);
                  this.podcastService.audio?.removeEventListener('pause', onPause);
                  this.podcastService.audio?.removeEventListener('loadstart', onLoadStart);
                  this.podcastService.audio?.removeEventListener('canplay', onCanPlay);
                  this.podcastService.audio?.removeEventListener('ended', onEnded);
                });
                
                console.log('Audio events configured successfully');
                
                // Verificar el estado actual del audio y actualizar el estado inicial
                setTimeout(() => {
                  if (this.podcastService.audio) {
                    console.log('Checking audio state - readyState:', this.podcastService.audio.readyState, 'paused:', this.podcastService.audio.paused);
                    
                    if (this.podcastService.audio.readyState >= 2) { // HAVE_CURRENT_DATA
                      if (!this.podcastService.audio.paused) {
                        console.log('Audio is already playing, updating state to playing');
                        this.status$$.next('playing');
                      } else {
                        console.log('Audio is paused, updating state to not-playing');
                        this.status$$.next('not-playing');
                      }
                    } else {
                      console.log('Audio is still loading, setting state to loading');
                      this.status$$.next('loading');
                    }
                  }
                }, 100);
              }
            };
            
            // Configurar eventos inmediatamente si el audio ya existe
            setupAudioEvents();
            
            // También configurar cuando se cree el audio en el servicio
            const originalCreate = this.podcastService.create.bind(this.podcastService);
            this.podcastService.create = (episode) => {
              const audio = originalCreate(episode);
              // Configurar eventos después de crear el audio
              setTimeout(() => {
                console.log('Audio created, setting up events...');
                setupAudioEvents();
              }, 100);
              return audio;
            };

            this.duration$ = interval(1000).pipe(map(() => this.podcastService.audio?.duration || 0));
            this.position$ = interval(1000).pipe(map(() => this.podcastService.audio?.currentTime || 0));
            this.positionDisplay$ = this.position$.pipe(map((currentPosition: number) => currentPosition < 0 ? '00:00' : this.display(currentPosition)));
            this.durationDisplay$ = this.duration$.pipe(map(duration => duration < 0 ? '--:--' : this.display(duration)));

            episode.previous$$.pipe(skip(1), take(1)).subscribe(episode => this.previous(episode));
            episode.next$$.pipe(skip(1), take(1)).subscribe(episode => this.next(episode));

            this.progress$ = combineLatest([this.position$, this.duration$])
              .pipe(map(([position, duration]: [position: number, duration: number]) => duration > 0 ? (position * 100) / duration : 0));

          }),
        ))).subscribe(async episode => { 
          this.episode$$.next(episode); 
          // Solo establecer estado de carga si no hay audio reproduciéndose
          if (!this.podcastService.audio || this.podcastService.audio.paused) {
            this.status$$.next('loading');
          }
          await this.play(episode); 
        });
    });
  }

  private display(seconds) {
    const format = val => `0${Math.floor(val)}`.slice(-2)
    const hours = seconds / 3600
    const minutes = (seconds % 3600) / 60
    return seconds > 3600 ? [hours, minutes, seconds % 60].map(format).join(':') : [minutes, seconds % 60].map(format).join(':');
  }

  public updateLikedItem(liked) {
    let newEpisode = this.episode$$.value;
    newEpisode.social.likes = liked.newItem.social.likes;
    this.episode$$.next(newEpisode);
  }

  public updateCommentedItem(count) {
    let newEpisode = this.episode$$.value;
    newEpisode.social.comments.count = count.count;
    this.episode$$.next(newEpisode)
  }


  async download(episode) {
    this.downloadStatus.next('downloading');
    const open = this.platformService.isIos() ? true : false;
    await this.documentService.downloadDoc(episode, open);
    this.downloadStatus.next('paused');
    if (this.platformService.isAndroid()) this.toast.info('La descarga se ha completado, revisa la carpeta de descargas');
  }

  async play(episode) {
    console.log('Play button clicked, episode:', episode);
    
    // Solo establecer estado de carga si no hay audio o si está pausado
    if (!this.podcastService.audio || this.podcastService.audio.paused) {
      this.status$$.next('loading');
    }
    
    try {
      await this.podcastService.play(episode);
      console.log('PodcastService play completed');
      // El estado 'playing' se establecerá automáticamente cuando se dispare el evento 'play'
      // del audio, no necesitamos establecerlo manualmente aquí
    } catch (error) {
      console.error('Error playing episode:', error);
      this.status$$.next('not-playing');
    }
  }

  pause(episode) {
    console.log('Pause button clicked, episode:', episode);
    this.podcastService.pause(episode);
    // El estado se actualizará automáticamente por los eventos del audio
  }

  async togglePlayPause(episode) {
    console.log('Toggle play/pause clicked, current status:', this.status$$.value);
    const currentStatus = this.status$$.value;
    
    // Verificar el estado real del audio
    const audioState = this.podcastService.audio ? 
      (this.podcastService.audio.paused ? 'paused' : 'playing') : 'no-audio';
    console.log('Actual audio state:', audioState);
    
    if (currentStatus === 'playing' || audioState === 'playing') {
      // Si está reproduciendo, pausar
      console.log('Pausing audio...');
      this.pause(episode);
    } else {
      // Si está pausado, cargando, o no reproduciendo, reproducir
      console.log('Playing audio...');
      await this.play(episode);
    }
  }

  stop(episode) {
    this.podcastService.destroyMusicControls();
  }

  rewind(episode) {
    this.podcastService.rewind(episode, 10);
  }

  forward(episode) {
    this.podcastService.forward(episode, 10);
  }

  seekTo($event, episode) {
    console.log('Seek to:', $event, 'episode:', episode);
    const duration = this.podcastService.audio?.duration || 0;
    const seekTime = (duration * $event) / 100;
    console.log('Seeking to time:', seekTime, 'seconds');
    this.podcastService.seekTo(episode, seekTime);
  }

  next(episode) {
    this.podcastService.stop();
    this.episodeSubscription.unsubscribe();
    if (this.statusUpdateSubscription) {
      this.statusUpdateSubscription.unsubscribe();
    }

    const episodes: number[] = episode.component.items.itemList.map(item => item.id);
    const index = episodes.findIndex(id => id === episode.id);
    let targetEpisodeId = episodes[index + 1];

    if (index === episodes.length - 1) {
      targetEpisodeId = episodes[0];
    }

    this.router.navigate([], { relativeTo: this.route, queryParams: { componentId: this.componentId, episodeId: targetEpisodeId } });
  }

  previous(episode) {
    this.podcastService.stop();
    this.episodeSubscription.unsubscribe();
    if (this.statusUpdateSubscription) {
      this.statusUpdateSubscription.unsubscribe();
    }

    const episodes: number[] = episode.component.items.itemList.map(item => item.id);
    const index = episodes.findIndex(id => id === episode.id);
    let targetEpisodeId = episodes[index - 1];

    if (index === 0) {
      targetEpisodeId = episodes[episodes.length - 1];
    }

    this.router.navigate([], { relativeTo: this.route, queryParams: { componentId: this.componentId, episodeId: targetEpisodeId } });
  }
}
